import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Network, Plus, Settings, Trash2 } from 'lucide-react';

import ConfigurarModal from '../../components/Modals/CanalModal/ConfigurarModal/ConfigurarModal';
import ConectarModal from '../../components/Modals/CanalModal/ConectarModal/ConectarModal';
import Page from '../../layout/IntegraAppPage';
import { useAuth } from '../../hooks/auth';
import backend from '../../services/backend';

interface DadosCanal {
  id: number;
  nome: string;
  tipo: 'Marketplace' | 'E-commerce';
  status?: 'Ativo' | 'Inativo' | 'Pendente';
  ambiente?: string;
  sincronizacao?: string;
  descricao?: string;
  tipoAcao?: 'backend' | 'modal';
}

const CHAVE_ARMAZENAMENTO_INTEGRACOES = 'canais-integracoes-ativas';
const CHAVE_ARMAZENAMENTO_CANAL_PENDENTE = 'canais-canal-pendente';

const ENDPOINT_DESCONECTAR_CANAL =
  process.env.REACT_APP_BACKEND_DESCONECTAR_CANAL_ENDPOINT ||
  '/integrations/disconnect';

const CHAVES_TOKEN_ACESSO = ['@DS850:token', 'meu:token', 'token'];

const INTEGRACAO_MERCADO_LIVRE: DadosCanal = {
  id: 1,
  nome: 'Mercado Livre',
  tipo: 'Marketplace',
  status: 'Ativo',
  ambiente: 'Produção',
  sincronizacao: '30min',
};

const carregarIntegracoesSalvas = (): DadosCanal[] => {
  const conteudo = localStorage.getItem(CHAVE_ARMAZENAMENTO_INTEGRACOES);

  if (!conteudo) return [];

  try {
    const parsed = JSON.parse(conteudo);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const salvarIntegracoes = (dados: DadosCanal[]) => {
  localStorage.setItem(CHAVE_ARMAZENAMENTO_INTEGRACOES, JSON.stringify(dados));
};

const salvarCanalPendente = (canal: DadosCanal) => {
  localStorage.setItem(
    CHAVE_ARMAZENAMENTO_CANAL_PENDENTE,
    JSON.stringify(canal),
  );
};

const carregarCanalPendente = (): DadosCanal | null => {
  const conteudo = localStorage.getItem(CHAVE_ARMAZENAMENTO_CANAL_PENDENTE);

  if (!conteudo) return null;

  try {
    return JSON.parse(conteudo);
  } catch {
    return null;
  }
};

const limparCanalPendente = () => {
  localStorage.removeItem(CHAVE_ARMAZENAMENTO_CANAL_PENDENTE);
};

const normalizarPlataforma = (nomePlataforma: string): string =>
  nomePlataforma
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const obterTokenAcesso = (): string | null => {
  const chaveComToken = CHAVES_TOKEN_ACESSO.find(chave =>
    localStorage.getItem(chave),
  );

  if (!chaveComToken) return null;

  return localStorage.getItem(chaveComToken);
};

const Canais: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const history = useHistory();
  const toast = useToast();

  const modalConfig = useDisclosure();
  const modalConectar = useDisclosure();

  const fundoCard = useColorModeValue('white', 'gray.700');
  const corBorda = useColorModeValue('gray.200', 'gray.600');
  const corTexto = useColorModeValue('gray.800', 'white');
  const corTextoSecundario = useColorModeValue('gray.600', 'gray.400');
  const fundoHoverCard = useColorModeValue('gray.50', 'gray.600');

  const [carregando, setCarregando] = useState(true);
  const [canalSelecionado, setCanalSelecionado] = useState<DadosCanal | null>(
    null,
  );
  const [integracoes, setIntegracoes] = useState<DadosCanal[]>([]);
  const [canalDesconectandoId, setCanalDesconectandoId] = useState<
    number | null
  >(null);

  const atualizarIntegracoes = useCallback(() => {
    setCarregando(true);
    setIntegracoes(carregarIntegracoesSalvas());
    setCarregando(false);
  }, []);

  useEffect(() => {
    atualizarIntegracoes();
  }, [atualizarIntegracoes]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const canalPendente = carregarCanalPendente();
    const houveErroNoRetorno = params.has('error');

    const retornoMercadoLivre =
      params.has('code') ||
      params.get('ml') === 'success' ||
      params.get('integracao') === 'mercadolivre' ||
      Boolean(canalPendente && !houveErroNoRetorno);

    if (houveErroNoRetorno) {
      limparCanalPendente();
      return;
    }

    if (!retornoMercadoLivre) return;

    const canalIntegrado: DadosCanal = {
      id: canalPendente?.id ?? INTEGRACAO_MERCADO_LIVRE.id,
      nome: canalPendente?.nome ?? INTEGRACAO_MERCADO_LIVRE.nome,
      tipo: canalPendente?.tipo ?? INTEGRACAO_MERCADO_LIVRE.tipo,
      status: 'Ativo',
      ambiente: 'Produção',
      sincronizacao: '30min',
    };

    setIntegracoes(anteriores => {
      const jaConectado = anteriores.some(
        item => item.nome === canalIntegrado.nome,
      );

      if (jaConectado) return anteriores;

      const atualizadas = [...anteriores, canalIntegrado];
      salvarIntegracoes(atualizadas);
      return atualizadas;
    });

    limparCanalPendente();
  }, [location.search]);

  const canaisDisponiveis: DadosCanal[] = [
    {
      id: 1,
      nome: 'Mercado Livre',
      tipo: 'Marketplace',
      descricao: 'Líder em vendas na América Latina com integração total.',
      tipoAcao: 'backend',
    },
  ];

  const canaisDisponiveisFiltrados = canaisDisponiveis.filter(
    canal => !integracoes.some(integracao => integracao.nome === canal.nome),
  );

  const handleEditar = (item: DadosCanal) => {
    setCanalSelecionado(item);
    modalConfig.onOpen();
  };

  const handleConectar = (canal: DadosCanal) => {
    if (canal.tipoAcao === 'backend') {
      if (!user?.empresa_id || !user?.filial_id) {
        toast({
          title: 'Empresa ou filial não identificada.',
          description: 'Faça login novamente antes de conectar o canal.',
          status: 'error',
          position: 'bottom-right',
          isClosable: true,
        });
        return;
      }

      salvarCanalPendente(canal);

      const baseUrl =
        process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL;

      window.location.href = `${baseUrl}/ml/auth?empresa_id=${user.empresa_id}&filial_id=${user.filial_id}`;
      return;
    }

    setCanalSelecionado(canal);
    modalConectar.onOpen();
  };

  const handleDesconectar = async (canal: DadosCanal) => {
    try {
      setCanalDesconectandoId(canal.id);

      const plataforma = normalizarPlataforma(canal.nome);
      const tokenAcesso = obterTokenAcesso();

      if (!tokenAcesso) {
        throw new Error('Token de acesso não encontrado');
      }

      const body = {
        plataforma,
        platform: plataforma,
      };

      try {
        await backend.post(ENDPOINT_DESCONECTAR_CANAL, body);
      } catch {
        await backend.delete(ENDPOINT_DESCONECTAR_CANAL, {
          data: body,
        });
      }

      setIntegracoes(anteriores => {
        const atualizadas = anteriores.filter(item => item.id !== canal.id);
        salvarIntegracoes(atualizadas);
        return atualizadas;
      });

      toast({
        title: `${canal.nome} desconectado com sucesso.`,
        status: 'success',
        position: 'bottom-right',
        isClosable: true,
      });
    } catch {
      toast({
        title: 'Não foi possível desconectar o canal.',
        description:
          'Verifique se o endpoint de desconexão está configurado no backend.',
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    } finally {
      setCanalDesconectandoId(null);
    }
  };

  let conteudoIntegracoesAtivas: ReactNode;

  if (carregando) {
    conteudoIntegracoesAtivas = (
      <>
        <Box h="180px" rounded="2xl" bg={corBorda} opacity={0.45} />
        <Box h="180px" rounded="2xl" bg={corBorda} opacity={0.45} />
        <Box h="180px" rounded="2xl" bg={corBorda} opacity={0.45} />
      </>
    );
  } else if (integracoes.length === 0) {
    conteudoIntegracoesAtivas = (
      <Box
        border="1px dashed"
        borderColor={corBorda}
        rounded="2xl"
        p={8}
        textAlign="center"
        gridColumn="1 / -1"
      >
        <Text fontWeight="bold" color={corTexto}>
          Não há integrações ativas
        </Text>
        <Text mt={2} fontSize="sm" color={corTextoSecundario}>
          Conecte um canal na seção abaixo para começar.
        </Text>
      </Box>
    );
  } else {
    conteudoIntegracoesAtivas = integracoes.map(item => (
      <Box
        key={item.id}
        border="1px solid"
        borderColor={corBorda}
        rounded="2xl"
        p={5}
        transition="all 0.3s"
        _hover={{
          shadow: 'lg',
          transform: 'translateY(-4px)',
          borderColor: 'blue.500',
          bg: fundoHoverCard,
        }}
      >
        <Flex justify="space-between" align="flex-start">
          <HStack spacing={3}>
            <Flex
              bg="blue.500"
              p={2.5}
              rounded="xl"
              align="center"
              justify="center"
            >
              <Icon as={Network} color="white" size={20} />
            </Flex>

            <VStack align="start" spacing={0}>
              <Text fontWeight="bold" color={corTexto} noOfLines={1}>
                {item.nome}
              </Text>
              <Badge
                colorScheme="blue"
                borderRadius="full"
                px={2}
                fontSize="10px"
              >
                {item.tipo}
              </Badge>
            </VStack>
          </HStack>

          <Badge
            colorScheme="green"
            variant="solid"
            borderRadius="full"
            px={3}
            py={0.5}
            fontSize="11px"
          >
            {item.status}
          </Badge>
        </Flex>

        <Divider my={4} opacity={0.6} />

        <Flex justify="space-between" fontSize="xs" mb={5}>
          <VStack align="start" spacing={1} color={corTextoSecundario}>
            <Text>Ambiente:</Text>
            <Text>Sincronização:</Text>
          </VStack>

          <VStack
            align="end"
            spacing={1}
            color={corTexto}
            fontWeight="semibold"
          >
            <Text>{item.ambiente}</Text>
            <Text>{item.sincronizacao}</Text>
          </VStack>
        </Flex>

        <HStack spacing={3}>
          <Button
            size="sm"
            flex="1"
            variant="outline"
            leftIcon={<Settings size={14} />}
            onClick={() => handleEditar(item)}
          >
            Configurar
          </Button>

          <Button
            size="sm"
            colorScheme="red"
            variant="outline"
            onClick={() => handleDesconectar(item)}
            isLoading={canalDesconectandoId === item.id}
            isDisabled={canalDesconectandoId !== null}
          >
            <Icon as={Trash2} size={14} />
          </Button>
        </HStack>
      </Box>
    ));
  }

  return (
    <Page>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" mb={1} textAlign="left">
            Canais
          </Heading>
        </Box>

        <Button
          leftIcon={<Settings size={16} />}
          colorScheme="blue"
          size="sm"
          onClick={() => history.push('/tokens/integra')}
        >
          Gerenciar API
        </Button>
      </Flex>

      <VStack spacing={12} align="stretch" pb="100px">
        <Box
          bg={fundoCard}
          borderWidth="1px"
          borderColor={corBorda}
          rounded="2xl"
          p={6}
          shadow="sm"
        >
          <Flex justify="space-between" align="center" mb={6}>
            <VStack align="start" spacing={1}>
              <Heading size="md" color={corTexto}>
                Integrações Ativas
              </Heading>
              <Text fontSize="sm" color={corTextoSecundario}>
                Gerencie as conexões que estão sincronizando dados no momento.
              </Text>
            </VStack>
          </Flex>

          <SimpleGrid columns={[1, null, 2, 3]} spacing={6}>
            {conteudoIntegracoesAtivas}
          </SimpleGrid>
        </Box>

        <Box
          bg={fundoCard}
          borderWidth="1px"
          borderColor={corBorda}
          rounded="2xl"
          p={6}
          shadow="sm"
        >
          <Heading size="md" mb={6} color={corTexto}>
            Canais Disponíveis
          </Heading>

          <SimpleGrid columns={[1, null, 2, 4]} spacing={6}>
            {canaisDisponiveisFiltrados.map(canal => (
              <Box
                key={canal.id}
                border="1px solid"
                borderColor={corBorda}
                rounded="2xl"
                p={5}
                _hover={{ borderColor: 'blue.500', bg: fundoHoverCard }}
                transition="0.2s"
              >
                <VStack align="start" spacing={3}>
                  <Text fontWeight="bold" color={corTexto}>
                    {canal.nome}
                  </Text>

                  <Text fontSize="xs" color={corTextoSecundario} noOfLines={2}>
                    {canal.descricao}
                  </Text>

                  <Button
                    size="sm"
                    leftIcon={<Plus size={14} />}
                    colorScheme="blue"
                    width="full"
                    onClick={() => handleConectar(canal)}
                  >
                    Conectar
                  </Button>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </Box>
      </VStack>

      <ConfigurarModal
        isOpen={modalConfig.isOpen}
        onClose={modalConfig.onClose}
        canal={canalSelecionado}
      />

      <ConectarModal
        isOpen={modalConectar.isOpen}
        onClose={modalConectar.onClose}
        canal={canalSelecionado}
      />
    </Page>
  );
};

export default Canais;
